import { API, WS_URL } from '@/lib/api';
import { useState, useEffect, useRef, useCallback } from "react";
import { getErrorMessage } from "@/lib/utils";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Wifi, WifiOff, Briefcase, Clock, Star, CheckCircle2, MapPin, DollarSign, Activity, Shield, MessageCircle, Package, Car, Truck, Home, Wrench, AlertTriangle, X, Check, Timer, TrendingUp, Zap, ChevronRight, Navigation, Flame, Brain, Headphones, Route, ExternalLink, Wallet, ArrowUpRight, CreditCard, CircleDollarSign, Heart, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import Map, { Marker, NavigationControl } from "react-map-gl";
import ReviewModal from "@/components/ReviewModal";
import DirectionsMapOverlay from "@/components/DirectionsMapOverlay";


const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

const SERVICE_ICONS = {
  cargo_delivery: Truck, courier: Package, towing: Car,
  movers: Home, laborer: Briefcase, roadside_assistance: Wrench,
};
const SERVICE_LABELS = {
  cargo_delivery: "Cargo Delivery", courier: "Courier", towing: "Towing",
  movers: "Movers", laborer: "Laborer", roadside_assistance: "Roadside Assistance",
};

const RequestCard = ({ request, onAccept, onDecline, accepting }) => {
  const [timeLeft, setTimeLeft] = useState(0);
  const Icon = SERVICE_ICONS[request.service_type] || Package;
  useEffect(() => {
    const expires = new Date(request.expires_at).getTime();
    const tick = () => {
      const remaining = Math.max(0, Math.floor((expires - Date.now()) / 1000));
      setTimeLeft(remaining);
      if (remaining <= 0) onDecline(request.request_id);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [request.expires_at, request.request_id, onDecline]);
  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  return (
    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-white rounded-2xl border-2 border-emerald-200 p-5 shadow-lg" data-testid={`request-card-${request.request_id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center"><Icon className="w-5 h-5 text-emerald-700" /></div>
          <div>
            <p className="font-semibold text-emerald-950">{SERVICE_LABELS[request.service_type] || request.service_type}</p>
            <p className="text-xs text-stone-500">{request.customer_name || "Customer"}</p>
          </div>
        </div>
        <div className="text-right">
          <div className={`text-lg font-bold ${timeLeft < 30 ? "text-red-600" : "text-emerald-700"}`}><Timer className="w-4 h-4 inline mr-1" />{minutes}:{seconds.toString().padStart(2, "0")}</div>
        </div>
      </div>
      {request.pickup_address && <div className="flex items-start gap-2 mb-1 text-sm text-stone-600"><MapPin className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" /><span className="line-clamp-1">{request.pickup_address}</span></div>}
      {request.dropoff_address && <div className="flex items-start gap-2 mb-3 text-sm text-stone-600"><MapPin className="w-4 h-4 text-red-500 shrink-0 mt-0.5" /><span className="line-clamp-1">{request.dropoff_address}</span></div>}
      {request.eta_minutes && <Badge className="mb-3 bg-emerald-50 text-emerald-700 border-emerald-200"><Navigation className="w-3 h-3 mr-1" /> {request.eta_minutes} min · {request.distance_km} km</Badge>}
      <div className="flex gap-2">
        <Button variant="outline" className="flex-1 border-red-200 text-red-600 hover:bg-red-50" onClick={() => onDecline(request.request_id)} data-testid={`decline-${request.request_id}`}><X className="w-4 h-4 mr-1" /> Decline</Button>
        <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" disabled={accepting} onClick={() => onAccept(request.request_id)} data-testid={`accept-${request.request_id}`}><Check className="w-4 h-4 mr-1" /> {accepting ? "Accepting..." : "Accept"}</Button>
      </div>
    </motion.div>
  );
};

const SkeletonCard = () => (
  <div className="bg-white rounded-2xl p-5 border border-stone-100 animate-pulse">
    <div className="w-8 h-8 bg-stone-200 rounded mb-3" />
    <div className="w-16 h-6 bg-stone-200 rounded mb-2" />
    <div className="w-20 h-3 bg-stone-100 rounded" />
  </div>
);

const ProfessionalDashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isOnline, setIsOnline] = useState(false);
  const [requests, setRequests] = useState([]);
  const [activeJobs, setActiveJobs] = useState([]);
  const [accepting, setAccepting] = useState(false);
  const [dailyStats, setDailyStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [nearbyPros, setNearbyPros] = useState([]);
  const [reviewRequest, setReviewRequest] = useState(null);
  const [directionsJob, setDirectionsJob] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [connectStatus, setConnectStatus] = useState(null);
  const [settingUpPayout, setSettingUpPayout] = useState(false);
  const [tipsData, setTipsData] = useState(null);
  const [withdrawingTips, setWithdrawingTips] = useState(false);
  const wsRef = useRef(null);
  const locationWatchRef = useRef(null);
  const [userLocation, setUserLocation] = useState(null);
  const [viewState, setViewState] = useState({ latitude: 43.65, longitude: -79.38, zoom: 12 });

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const loc = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
          setUserLocation(loc);
          setViewState((v) => ({ ...v, ...loc }));
        },
        () => {}
      );
    }
  }, []);

  // Fetch daily stats & auto-set online
  const autoOnlineDone = useRef(false);
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API}/professional/daily-stats`, { withCredentials: true });
        setDailyStats(res.data);
        setIsOnline(res.data.is_online);
        // Auto-set online on first load
        if (!autoOnlineDone.current && !res.data.is_online) {
          autoOnlineDone.current = true;
          try {
            let payload = { is_online: true };
            if (navigator.geolocation) {
              const pos = await new Promise((resolve, reject) => navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 5000 })).catch(() => null);
              if (pos) { payload.latitude = pos.coords.latitude; payload.longitude = pos.coords.longitude; }
            }
            await axios.patch(`${API}/user/professional/online`, payload, { withCredentials: true });
            setIsOnline(true);
          } catch { /* ignore auto-online failure */ }
        } else {
          autoOnlineDone.current = true;
        }
      } catch { /* ignore */ }
      setStatsLoading(false);
    };
    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  // Periodic location update while online
  useEffect(() => {
    if (!isOnline) {
      if (locationWatchRef.current) { navigator.geolocation.clearWatch(locationWatchRef.current); locationWatchRef.current = null; }
      return;
    }
    if (!navigator.geolocation) return;
    locationWatchRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const loc = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
        setUserLocation(loc);
        setViewState((prev) => ({ ...prev, ...loc }));
        axios.patch(`${API}/user/professional/location`, loc, { withCredentials: true }).catch(() => {});
      }, () => {}, { enableHighAccuracy: true, maximumAge: 30000, timeout: 15000 }
    );
    return () => { if (locationWatchRef.current) { navigator.geolocation.clearWatch(locationWatchRef.current); locationWatchRef.current = null; } };
  }, [isOnline]);

  // WebSocket for live requests
  useEffect(() => {
    if (!isOnline || !user) return;
    // Fetch existing pending requests on connect
    axios.get(`${API}/service-requests/active`, { withCredentials: true })
      .then(res => {
        if (res.data.requests?.length) setRequests(res.data.requests);
        if (res.data.active_jobs) setActiveJobs(res.data.active_jobs);
      })
      .catch(() => {});
    const token = document.cookie.split(";").find((c) => c.trim().startsWith("session_token="))?.split("=")[1];
    if (!token) return;
    let ws = null;
    let reconnectTimeout = null;
    let isMounted = true;

    const connectWs = () => {
      if (!isMounted) return;
      ws = new WebSocket(`${WS_URL}/api/ws/professional/${token}`);
      wsRef.current = ws;
      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === "new_request") setRequests((prev) => [...prev.filter((r) => r.request_id !== data.request.request_id), data.request]);
        else if (data.type === "active_requests" && data.requests) setRequests(data.requests);
        else if (data.type === "request_expired") setRequests((prev) => prev.filter((r) => r.request_id !== data.request_id));
        else if (data.type === "active_jobs") setActiveJobs(data.jobs || []);
        else if (data.type === "job_completed") { setReviewRequest(data); setActiveJobs((prev) => prev.filter((j) => j.request_id !== data.request_id)); }
        else if (data.type === "tip_received") {
          toast.success(`You received a $${data.amount.toFixed(2)} tip from ${data.from_name}!`);
          axios.get(`${API}/professional/tips`, { withCredentials: true })
            .then(res => { if (res.data) setTipsData(res.data); })
            .catch(() => {});
        }
      };
      ws.onopen = () => {
        // Keep-alive ping every 25s
        ws._pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "ping" }));
        }, 25000);
      };
      ws.onclose = () => {
        wsRef.current = null;
        clearInterval(ws._pingInterval);
        if (isMounted) reconnectTimeout = setTimeout(connectWs, 3000);
      };
      ws.onerror = () => { ws.close(); };
    };

    connectWs();

    // Poll every 10s as WS fallback
    const pollInterval = setInterval(() => {
      axios.get(`${API}/service-requests/active`, { withCredentials: true })
        .then(res => {
          if (res.data.requests) setRequests(res.data.requests);
          if (res.data.active_jobs) setActiveJobs(res.data.active_jobs);
        })
        .catch(() => {});
    }, 10000);
    return () => { isMounted = false; clearTimeout(reconnectTimeout); if (ws) { clearInterval(ws._pingInterval); ws.close(); } clearInterval(pollInterval); };
  }, [isOnline, user]);

  // Fetch earnings & connect status & tips
  useEffect(() => {
    const fetchEarnings = async () => {
      try {
        const [earningsRes, connectRes, tipsRes] = await Promise.all([
          axios.get(`${API}/professional/earnings`, { withCredentials: true }).catch(() => ({ data: null })),
          axios.get(`${API}/stripe/connect/status`, { withCredentials: true }).catch(() => ({ data: null })),
          axios.get(`${API}/professional/tips`, { withCredentials: true }).catch(() => ({ data: null })),
        ]);
        if (earningsRes.data) setEarnings(earningsRes.data);
        if (connectRes.data) setConnectStatus(connectRes.data);
        if (tipsRes.data) setTipsData(tipsRes.data);
      } catch {}
    };
    fetchEarnings();
  }, []);

  // Handle connect query params (return from Stripe onboarding)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("connect") === "success") {
      toast.success("Stripe Connect setup successful!");
      axios.get(`${API}/stripe/connect/status`, { withCredentials: true })
        .then(res => setConnectStatus(res.data))
        .catch(() => {});
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("connect") === "refresh") {
      toast.info("Please complete the Stripe onboarding process");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const handleSetupPayouts = async () => {
    setSettingUpPayout(true);
    try {
      // Create account if not exists
      if (!connectStatus?.connected) {
        await axios.post(`${API}/stripe/connect/create-account`, {}, { withCredentials: true });
      }
      // Get onboarding link
      const res = await axios.post(`${API}/stripe/connect/account-link`, { origin_url: window.location.origin }, { withCredentials: true });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to set up payouts"));
    }
    setSettingUpPayout(false);
  };

  const toggleOnline = async (checked) => {
    try {
      let payload = { is_online: checked };
      if (checked && navigator.geolocation) {
        try {
          const pos = await new Promise((resolve, reject) => navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 10000 }));
          payload.latitude = pos.coords.latitude;
          payload.longitude = pos.coords.longitude;
          setUserLocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
          setViewState((prev) => ({ ...prev, latitude: pos.coords.latitude, longitude: pos.coords.longitude }));
        } catch { /* continue without coords */ }
      }
      await axios.patch(`${API}/user/professional/online`, payload, { withCredentials: true });
      setIsOnline(checked);
      toast.success(checked ? "You're now online and available!" : "You're now offline");
      if (!checked && wsRef.current) { wsRef.current.close(); setRequests([]); }
    } catch (err) { toast.error(getErrorMessage(err, "Failed to update status")); }
  };

  const handleAccept = async (requestId) => {
    setAccepting(true);
    try {
      const res = await axios.post(`${API}/service-requests/${requestId}/accept`, {}, { withCredentials: true });
      setRequests((prev) => prev.filter((r) => r.request_id !== requestId));
      toast.success("Job accepted!");
      // Add accepted job to active jobs with location data
      const acceptedData = res.data;
      if (acceptedData.pickup_lat && acceptedData.pickup_lng) {
        const newJob = {
          request_id: requestId,
          service_type: acceptedData.service_type,
          customer_name: acceptedData.customer_name,
          pickup_address: acceptedData.pickup_address,
          dropoff_address: acceptedData.dropoff_address,
          pickup_lat: acceptedData.pickup_lat,
          pickup_lng: acceptedData.pickup_lng,
          status: "accepted",
          trip_status: "en_route",
        };
        setActiveJobs((prev) => [newJob, ...prev.filter(j => j.request_id !== requestId)]);
        setDirectionsJob(newJob);
      }
      const formData = new FormData();
      formData.append("request_id", requestId);
      await axios.post(`${API}/wallet/earn`, formData, { withCredentials: true }).catch(() => {});
    } catch (err) { toast.error(getErrorMessage(err, "Failed to accept request")); }
    setAccepting(false);
  };

  const handleMarkArrived = async (requestId) => {
    try {
      await axios.post(`${API}/service-requests/${requestId}/arrived`, {}, { withCredentials: true });
      setActiveJobs((prev) => prev.map(j => j.request_id === requestId ? { ...j, trip_status: "arrived" } : j));
      toast.success("Marked as arrived!");
    } catch (err) { toast.error(getErrorMessage(err, "Failed to mark arrived")); }
  };

  const handleCompleteJob = async (requestId) => {
    try {
      const res = await axios.post(`${API}/service-requests/${requestId}/complete`, {}, { withCredentials: true });
      setActiveJobs((prev) => prev.filter(j => j.request_id !== requestId));
      setDirectionsJob(null);
      toast.success("Job completed!");
      if (res.data.review_eligible) {
        const sr = activeJobs.find(j => j.request_id === requestId);
        setReviewRequest({ request_id: requestId, customer_name: sr?.customer_name || "Customer" });
      }
    } catch (err) { toast.error(getErrorMessage(err, "Failed to complete job")); }
  };

  const handleDecline = useCallback((requestId) => { setRequests((prev) => prev.filter((r) => r.request_id !== requestId)); }, []);
  const handleChat = (requestId) => navigate(`/chat/${requestId}`);

  if (!user) return null;
  if (user.professional_status !== "approved") {
    return (
      <div className="min-h-screen bg-stone-50"><Navbar />
        <div className="pt-32 px-4 max-w-lg mx-auto text-center">
          <Shield className="w-16 h-16 text-stone-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-emerald-950 mb-2">Verification Required</h2>
          <p className="text-stone-500 mb-6">Complete your professional verification to access the Work Hub.</p>
          <Button className="bg-emerald-950 hover:bg-emerald-900 text-white rounded-full px-8" onClick={() => navigate("/professional/verify")} data-testid="start-verification-btn">Start Verification</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="professional-dashboard">
      <Navbar />
      <div className="pt-20">
        {/* Status Bar */}
        <div className={`sticky top-20 z-50 transition-colors ${isOnline ? "bg-green-500" : "bg-stone-500"}`} data-testid="status-bar">
          <div className="max-w-5xl mx-auto px-4 py-2.5 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              {isOnline ? <Wifi className="w-4 h-4 text-white shrink-0" /> : <WifiOff className="w-4 h-4 text-white shrink-0" />}
              <span className="text-white text-sm font-medium truncate">{isOnline ? "Online - Receiving Requests" : "Offline - Not Receiving Requests"}</span>
            </div>
            <Switch checked={isOnline} onCheckedChange={toggleOnline} className="shrink-0" data-testid="online-toggle" />
          </div>
        </div>

        <section className="pb-20 px-4 pt-6">
          <div className="max-w-5xl mx-auto">
            {/* Header */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-emerald-950">Work Hub</h1>
                <p className="text-stone-500 text-sm mt-1">Welcome back, {user.name?.split(" ")[0]}</p>
              </div>
              <Badge className="bg-green-100 text-green-800 flex items-center gap-1" data-testid="verified-badge">
                <CheckCircle2 className="w-3 h-3" /> Verified
              </Badge>
            </motion.div>

            {/* Daily Summary — Earnings at top */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="daily-summary">
              {statsLoading ? (
                Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)
              ) : (
                <>
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-2xl p-5 text-white">
                    <DollarSign className="w-7 h-7 mb-1 opacity-80" />
                    <p className="text-2xl font-bold" data-testid="earnings-today">${(dailyStats?.earnings_today || 0).toFixed(2)}</p>
                    <p className="text-xs text-emerald-200">Earned Today</p>
                  </motion.div>
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="bg-white rounded-2xl p-5 border border-stone-100">
                    <TrendingUp className="w-7 h-7 text-blue-500 mb-1" />
                    <p className="text-2xl font-bold text-emerald-950" data-testid="earnings-week">${(dailyStats?.earnings_week || 0).toFixed(2)}</p>
                    <p className="text-xs text-stone-500">This Week</p>
                  </motion.div>
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-2xl p-5 border border-stone-100">
                    <CheckCircle2 className="w-7 h-7 text-green-500 mb-1" />
                    <p className="text-2xl font-bold text-emerald-950" data-testid="completed-today">{dailyStats?.completed_today || 0}</p>
                    <p className="text-xs text-stone-500">Jobs Today</p>
                  </motion.div>
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="bg-white rounded-2xl p-5 border border-stone-100">
                    <Star className="w-7 h-7 text-amber-500 mb-1" />
                    <p className="text-2xl font-bold text-emerald-950" data-testid="avg-rating">{dailyStats?.avg_rating > 0 ? dailyStats.avg_rating.toFixed(1) : "--"}</p>
                    <p className="text-xs text-stone-500">Rating ({dailyStats?.review_count || 0})</p>
                  </motion.div>
                </>
              )}
            </div>

            {/* Payouts & Earnings Card */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl border border-stone-100 p-5 mb-6" data-testid="payouts-card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-emerald-950 flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-emerald-600" /> Payouts & Earnings
                </h3>
                {connectStatus?.connected && connectStatus?.payouts_enabled ? (
                  <Badge className="bg-emerald-100 text-emerald-700" data-testid="payout-status-active">Payouts Active</Badge>
                ) : connectStatus?.connected ? (
                  <Badge className="bg-amber-100 text-amber-700" data-testid="payout-status-pending">Setup Pending</Badge>
                ) : (
                  <Badge className="bg-stone-100 text-stone-600" data-testid="payout-status-none">Not Connected</Badge>
                )}
              </div>

              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-emerald-50 rounded-xl p-3 text-center">
                  <p className="text-xl font-bold text-emerald-700" data-testid="total-earned">${(earnings?.wallet?.total_earned || 0).toFixed(2)}</p>
                  <p className="text-xs text-emerald-600">Total Earned</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-3 text-center">
                  <p className="text-xl font-bold text-blue-700" data-testid="total-paid-out">${(earnings?.total_paid_out || 0).toFixed(2)}</p>
                  <p className="text-xs text-blue-600">Paid Out</p>
                </div>
                <div className="bg-amber-50 rounded-xl p-3 text-center">
                  <p className="text-xl font-bold text-amber-700" data-testid="pending-balance">${(earnings?.pending_payout || 0).toFixed(2)}</p>
                  <p className="text-xs text-amber-600">Pending</p>
                </div>
              </div>

              {connectStatus?.connected && connectStatus?.payouts_enabled ? (
                <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 rounded-xl p-3">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Stripe Connect active — earnings auto-transfer to your bank after each job</span>
                </div>
              ) : (
                <Button
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl h-11 gap-2"
                  onClick={handleSetupPayouts}
                  disabled={settingUpPayout}
                  data-testid="setup-payouts-btn"
                >
                  {settingUpPayout ? (
                    <><CreditCard className="w-4 h-4 animate-pulse" /> Setting up...</>
                  ) : (
                    <><CreditCard className="w-4 h-4" /> Set Up Payouts with Stripe</>
                  )}
                </Button>
              )}

              {/* Recent Transfers */}
              {earnings?.transfers?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-stone-100">
                  <p className="text-xs font-medium text-stone-500 mb-2">Recent Payouts</p>
                  <div className="space-y-2 max-h-36 overflow-y-auto">
                    {earnings.transfers.slice(0, 5).map((t, i) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <ArrowUpRight className="w-3.5 h-3.5 text-emerald-500" />
                          <span className="text-stone-600">{new Date(t.created_at).toLocaleDateString()}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-stone-400 text-xs">-{(t.platform_fee || 0).toFixed(2)} fee</span>
                          <span className="font-medium text-emerald-700">${(t.amount || 0).toFixed(2)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>

            {/* Tips Card */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl border border-stone-100 p-5 mb-6" data-testid="tips-card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-emerald-950 flex items-center gap-2">
                  <Heart className="w-5 h-5 text-pink-500" /> Tips
                </h3>
                <Badge className="bg-pink-100 text-pink-700" data-testid="tips-today-badge">
                  Today: ${(tipsData?.tips_today || 0).toFixed(2)}
                </Badge>
              </div>

              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-pink-50 rounded-xl p-3 text-center">
                  <p className="text-xl font-bold text-pink-700" data-testid="tips-today-total">${(tipsData?.tips_today || 0).toFixed(2)}</p>
                  <p className="text-xs text-pink-600">Today ({tipsData?.tips_today_count || 0})</p>
                </div>
                <div className="bg-purple-50 rounded-xl p-3 text-center">
                  <p className="text-xl font-bold text-purple-700" data-testid="tips-all-time">${(tipsData?.tips_all_time || 0).toFixed(2)}</p>
                  <p className="text-xs text-purple-600">All Time</p>
                </div>
                <div className="bg-emerald-50 rounded-xl p-3 text-center">
                  <p className="text-xl font-bold text-emerald-700" data-testid="tips-withdrawable">${(tipsData?.withdrawable_balance || 0).toFixed(2)}</p>
                  <p className="text-xs text-emerald-600">Available</p>
                </div>
              </div>

              {(tipsData?.withdrawable_balance || 0) >= 1 && (
                <Button
                  onClick={async () => {
                    try {
                      setWithdrawingTips(true);
                      const res = await axios.post(`${API}/professional/tips/withdraw`, {}, { withCredentials: true });
                      if (res.data.success) {
                        toast.success(`$${res.data.amount.toFixed(2)} tips withdrawn to your Stripe account!`);
                        const tipsRes = await axios.get(`${API}/professional/tips`, { withCredentials: true });
                        if (tipsRes.data) setTipsData(tipsRes.data);
                      }
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Withdrawal failed");
                    } finally {
                      setWithdrawingTips(false);
                    }
                  }}
                  disabled={withdrawingTips}
                  className="w-full mb-4 bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white"
                  data-testid="withdraw-tips-btn"
                >
                  {withdrawingTips ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Withdrawing...</>
                  ) : (
                    <><Wallet className="w-4 h-4 mr-2" /> Withdraw ${(tipsData?.withdrawable_balance || 0).toFixed(2)} Tips</>
                  )}
                </Button>
              )}

              {tipsData?.recent_tips?.length > 0 ? (
                <div className="border-t border-stone-100 pt-3">
                  <p className="text-xs font-medium text-stone-500 mb-2">Recent Tips</p>
                  <div className="space-y-2 max-h-36 overflow-y-auto">
                    {tipsData.recent_tips.slice(0, 5).map((tip, i) => (
                      <div key={i} className="flex items-center justify-between text-sm" data-testid={`tip-entry-${i}`}>
                        <div className="flex items-center gap-2">
                          <Heart className="w-3.5 h-3.5 text-pink-400" />
                          <span className="text-stone-600">{tip.reviewer_name || "Customer"}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-stone-400 text-xs">{new Date(tip.created_at).toLocaleDateString()}</span>
                          <span className="font-semibold text-pink-600">${(tip.tip_amount || 0).toFixed(2)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-stone-400 text-center py-2">No tips yet — keep up the great work!</p>
              )}
            </motion.div>

            {/* Quick Actions */}
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-6" data-testid="quick-actions">
              <Button variant="outline" className="h-auto py-4 flex flex-col items-center gap-2 rounded-2xl border-stone-200 hover:border-emerald-300 hover:bg-emerald-50" onClick={() => navigate("/wallet")} data-testid="quick-earnings">
                <DollarSign className="w-5 h-5 text-emerald-600" />
                <span className="text-xs font-medium text-stone-700">Earnings</span>
              </Button>
              <Button variant="outline" className="h-auto py-4 flex flex-col items-center gap-2 rounded-2xl border-stone-200 hover:border-emerald-300 hover:bg-emerald-50" onClick={() => toggleOnline(!isOnline)} data-testid="quick-toggle">
                {isOnline ? <WifiOff className="w-5 h-5 text-red-500" /> : <Wifi className="w-5 h-5 text-emerald-600" />}
                <span className="text-xs font-medium text-stone-700">{isOnline ? "Go Offline" : "Go Online"}</span>
              </Button>
              <Button variant="outline" className="h-auto py-4 flex flex-col items-center gap-2 rounded-2xl border-stone-200 hover:border-orange-300 hover:bg-orange-50" onClick={() => navigate("/professional/heatmap")} data-testid="quick-heatmap">
                <Flame className="w-5 h-5 text-orange-500" />
                <span className="text-xs font-medium text-stone-700">Heatmap</span>
              </Button>
              <Button variant="outline" className="h-auto py-4 flex flex-col items-center gap-2 rounded-2xl border-stone-200 hover:border-violet-300 hover:bg-violet-50" onClick={() => navigate("/professional/earnings-insights")} data-testid="quick-insights">
                <Brain className="w-5 h-5 text-violet-500" />
                <span className="text-xs font-medium text-stone-700">AI Insights</span>
              </Button>
              <Button variant="outline" className="h-auto py-4 flex flex-col items-center gap-2 rounded-2xl border-stone-200 hover:border-teal-300 hover:bg-teal-50" onClick={() => navigate("/professional/support")} data-testid="quick-support">
                <Headphones className="w-5 h-5 text-teal-500" />
                <span className="text-xs font-medium text-stone-700">Support</span>
              </Button>
              <Button variant="outline" className="h-auto py-4 flex flex-col items-center gap-2 rounded-2xl border-stone-200 hover:border-emerald-300 hover:bg-emerald-50" onClick={() => document.getElementById("active-jobs")?.scrollIntoView({ behavior: "smooth" })} data-testid="quick-jobs">
                <Briefcase className="w-5 h-5 text-blue-600" />
                <span className="text-xs font-medium text-stone-700">Active Jobs</span>
              </Button>
            </div>

            {/* Promotions Banner */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-4 mb-6 flex items-center gap-3" data-testid="promotions-banner">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center shrink-0">
                <Zap className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-amber-900 text-sm">Peak Hours Bonus</p>
                <p className="text-xs text-amber-700">Earn 1.5x during 5PM - 9PM today. Complete 5+ trips for extra $10 bonus!</p>
              </div>
              <ChevronRight className="w-5 h-5 text-amber-400 shrink-0" />
            </motion.div>

            {/* Incoming Requests */}
            <AnimatePresence>
              {requests.length > 0 && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-6">
                  <h3 className="font-semibold text-emerald-950 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-500 animate-pulse" /> Incoming Requests ({requests.length})
                  </h3>
                  <div className="space-y-3">
                    {requests.map((req) => (<RequestCard key={req.request_id} request={req} onAccept={handleAccept} onDecline={handleDecline} accepting={accepting} />))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Active Jobs */}
            <div id="active-jobs">
              {activeJobs.length > 0 && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
                  <h3 className="font-semibold text-emerald-950 mb-3 flex items-center gap-2">
                    <Activity className="w-5 h-5 text-emerald-600" /> Active Jobs ({activeJobs.length})
                  </h3>
                  <div className="space-y-3">
                    {activeJobs.map((job) => {
                      const Icon = SERVICE_ICONS[job.service_type] || Package;
                      const hasCoords = job.pickup_lat && job.pickup_lng;
                      const isShowingDirections = directionsJob?.request_id === job.request_id;
                      const tripStatus = job.trip_status || "en_route";
                      const tripColors = { en_route: "bg-emerald-100 text-emerald-700", arriving: "bg-amber-100 text-amber-700", arrived: "bg-blue-100 text-blue-700" };
                      const tripLabels = { en_route: "En Route", arriving: "Arriving", arrived: "Arrived" };
                      return (
                        <div key={job.request_id} className="bg-white rounded-2xl border border-stone-100 overflow-hidden" data-testid={`active-job-${job.request_id}`}>
                          <div className="p-4">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-3 min-w-0">
                                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-emerald-700" /></div>
                                <div className="min-w-0">
                                  <p className="font-medium text-emerald-950">{SERVICE_LABELS[job.service_type] || job.service_type}</p>
                                  <p className="text-xs text-stone-500 line-clamp-1">{job.pickup_address}</p>
                                </div>
                              </div>
                              <Badge className={`${tripColors[tripStatus] || tripColors.en_route} text-xs`} data-testid={`trip-status-${job.request_id}`}>
                                {tripLabels[tripStatus] || "En Route"}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                              {hasCoords && (
                                <Button
                                  size="sm"
                                  className={`rounded-full gap-1 ${isShowingDirections ? "bg-stone-200 text-stone-700 hover:bg-stone-300" : "bg-emerald-600 hover:bg-emerald-700 text-white"}`}
                                  onClick={() => setDirectionsJob(isShowingDirections ? null : job)}
                                  data-testid={`directions-${job.request_id}`}
                                >
                                  <Navigation className="w-3.5 h-3.5" /> {isShowingDirections ? "Hide Map" : "Directions"}
                                </Button>
                              )}
                              {tripStatus !== "arrived" && (
                                <Button size="sm" className="rounded-full gap-1 bg-blue-600 hover:bg-blue-700 text-white" onClick={() => handleMarkArrived(job.request_id)} data-testid={`arrived-${job.request_id}`}>
                                  <CheckCircle2 className="w-3.5 h-3.5" /> I've Arrived
                                </Button>
                              )}
                              {tripStatus === "arrived" && (
                                <Button size="sm" className="rounded-full gap-1 bg-stone-800 hover:bg-stone-900 text-white" onClick={() => handleCompleteJob(job.request_id)} data-testid={`complete-${job.request_id}`}>
                                  <CheckCircle2 className="w-3.5 h-3.5" /> Complete Job
                                </Button>
                              )}
                              <Button size="sm" variant="outline" onClick={() => handleChat(job.request_id)} data-testid={`chat-${job.request_id}`}>
                                <MessageCircle className="w-4 h-4 mr-1" /> Chat
                              </Button>
                            </div>
                          </div>
                          {/* Embedded Directions Map */}
                          <AnimatePresence>
                            {isShowingDirections && hasCoords && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 350, opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                className="overflow-hidden border-t border-stone-100"
                              >
                                <div style={{ height: 350 }}>
                                  <DirectionsMapOverlay
                                    proLocation={userLocation ? { lat: userLocation.latitude, lng: userLocation.longitude } : null}
                                    customerLocation={{ lat: job.pickup_lat, lng: job.pickup_lng }}
                                    customerAddress={job.pickup_address}
                                    customerName={job.customer_name}
                                    onClose={() => setDirectionsJob(null)}
                                  />
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </div>

            {/* Service Area Map */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl border border-stone-100 overflow-hidden" data-testid="service-map">
              <div className="p-4 border-b border-stone-100">
                <h3 className="font-semibold text-emerald-950 flex items-center gap-2"><MapPin className="w-5 h-5 text-emerald-600" /> Your Service Area</h3>
              </div>
              <div style={{ height: "300px" }}>
                <Map ref={null} {...viewState} onMove={(e) => setViewState(e.viewState)} mapStyle="mapbox://styles/mapbox/streets-v12" mapboxAccessToken={MAPBOX_TOKEN} style={{ width: "100%", height: "100%" }}>
                  <NavigationControl position="top-right" showCompass={false} />
                  {userLocation && (
                    <Marker longitude={userLocation.longitude} latitude={userLocation.latitude} anchor="center">
                      <div className="relative"><div className="absolute w-10 h-10 bg-emerald-400/25 rounded-full animate-ping" /><div className="w-6 h-6 bg-emerald-600 rounded-full border-2 border-white shadow-lg flex items-center justify-center"><Car className="w-3 h-3 text-white" /></div></div>
                    </Marker>
                  )}
                </Map>
              </div>
            </motion.div>

          </div>
        </section>
      </div>
      {reviewRequest && (<ReviewModal requestId={reviewRequest.request_id} revieweeName={reviewRequest.customer_name} revieweeRole="customer" onClose={() => setReviewRequest(null)} />)}
    </div>
  );
};

export default ProfessionalDashboardPage;
